import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiRefreshCw } from "react-icons/fi";
import { Table, Modal, Spin, message } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import Header from '../../components/Header';
import Sidebar from "../../components/Sidebar";
import { plusicon } from '../../components/imagepath';
import inmuebleService from '../../services/inmuebleService';
import { useAuth } from '../../utils/AuthContext';
import useCanDelete from '../../hooks/useCanDelete';
import { BaseInput, BaseSelect, BaseTextarea, BaseButton } from '../../components/ui';

const TIPO_INMUEBLE_OPTIONS = [
  { value: 'Casa', label: 'Casa' },
  { value: 'Departamento', label: 'Departamento' },
  { value: 'Oficina', label: 'Oficina' },
  { value: 'Local Comercial', label: 'Local Comercial' },
  { value: 'Edificio', label: 'Edificio' },
  { value: 'Otro', label: 'Otro' },
];

const EMPTY_EDIT_FORM = {
  nombre: '',
  direccion: '',
  propietario_id: '',
  tipo_inmueble: '',
  cantidad_pisos: '',
  descripcion: '',
};

const InmuebleRegistros = () => {
  const { estaAutenticado } = useAuth();
  const canDelete = useCanDelete();
  const [inmuebles, setInmuebles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  
  // Estados para modales
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editFormData, setEditFormData] = useState(EMPTY_EDIT_FORM);
  const [inmuebleSeleccionado, setInmuebleSeleccionado] = useState(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [inmueblesFiltrados, setInmueblesFiltrados] = useState([]);
  const [propietarios, setPropietarios] = useState([]);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [updatedData, setUpdatedData] = useState(null);

  // Actualizar el ancho de la ventana al cambiar el tamaño
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verificar autenticación
  useEffect(() => {
    if (!estaAutenticado) {
      navigate('/login');
    }
  }, [estaAutenticado, navigate]);

  // Obtener los inmuebles y propietarios al cargar el componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Obtener inmuebles
        const inmueblesData = await inmuebleService.obtenerInmuebles();
        
        // Asegurarnos de que los datos sean un array
        const inmueblesArray = Array.isArray(inmueblesData) ? inmueblesData : [];
        setInmuebles(inmueblesArray);
        setInmueblesFiltrados(inmueblesArray);
        
        // Obtener propietarios para el formulario de edición
        try {
          const propietariosData = await inmuebleService.obtenerPropietarios();
          setPropietarios(Array.isArray(propietariosData) ? propietariosData : []);
        } catch (err) {
          setPropietarios([]);
        }
      } catch (error) {
        setError("Error al cargar los inmuebles. Por favor, intente de nuevo más tarde.");
        message.error("Error al cargar los inmuebles");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Efecto para aplicar filtro de búsqueda
  useEffect(() => {
    if (!searchText.trim()) {
      setInmueblesFiltrados(inmuebles);
      return;
    }

    const searchLower = searchText.toLowerCase();
    const filtered = inmuebles.filter(inmueble => 
      (inmueble.nombre && inmueble.nombre.toLowerCase().includes(searchLower)) || 
      (inmueble.direccion && inmueble.direccion.toLowerCase().includes(searchLower)) ||
      (inmueble.propietario_nombre && inmueble.propietario_nombre.toLowerCase().includes(searchLower)) ||
      (inmueble.tipo_inmueble && inmueble.tipo_inmueble.toLowerCase().includes(searchLower))
    );
    
    setInmueblesFiltrados(filtered);
  }, [searchText, inmuebles]);

  // Función para recargar los datos
  const refreshData = async () => {
    try {
      setLoading(true);
      const data = await inmuebleService.obtenerInmuebles();
      setInmuebles(Array.isArray(data) ? data : []);
      setInmueblesFiltrados(Array.isArray(data) ? data : []);
      message.success('Datos actualizados correctamente');
    } catch (error) {
      message.error('Error al actualizar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Acción para ver detalles de un inmueble
  const handleView = (inmuebleId) => {
    const inmueble = inmuebles.find(i => i.id === inmuebleId);
    if (inmueble) {
      setInmuebleSeleccionado(inmueble);
      setViewModalVisible(true);
    } else {
      message.error("No se encontró el inmueble seleccionado");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Acción para editar un inmueble
  const handleEdit = (inmuebleId) => {
    const inmueble = inmuebles.find(i => i.id === inmuebleId);
    if (inmueble) {
      setInmuebleSeleccionado(inmueble);
      setEditFormData({
        nombre: inmueble.nombre || '',
        direccion: inmueble.direccion || '',
        propietario_id: inmueble.propietario_id ?? '',
        tipo_inmueble: inmueble.tipo_inmueble || '',
        cantidad_pisos: inmueble.cantidad_pisos ?? '',
        descripcion: inmueble.descripcion || '',
      });
      setEditModalVisible(true);
    } else {
      message.error("No se encontró el inmueble seleccionado");
    }
  };

  // Guardar cambios de edición
  const handleSaveEdit = async () => {
    const camposRequeridos = ['nombre', 'direccion', 'propietario_id', 'tipo_inmueble', 'cantidad_pisos'];
    const faltantes = camposRequeridos.filter((campo) => !editFormData[campo] && editFormData[campo] !== 0);
    if (faltantes.length > 0) {
      message.error('Complete los campos obligatorios');
      return;
    }

    try {
      setLoadingSave(true);
      const inmuebleData = {
        nombre: editFormData.nombre,
        direccion: editFormData.direccion,
        propietario_id: editFormData.propietario_id,
        tipo_inmueble: editFormData.tipo_inmueble,
        cantidad_pisos: Number(editFormData.cantidad_pisos),
        descripcion: editFormData.descripcion || '',
      };
      
      await inmuebleService.actualizarInmueble(inmuebleSeleccionado.id, inmuebleData);
      
      setUpdatedData(inmuebleData);
      setEditModalVisible(false);
      setShowSuccessModal(true);
      
    } catch (err) {
      console.error("Error al actualizar inmueble:", err);
      message.error("Error al guardar cambios: " + (err.message || "Error desconocido"));
    } finally {
      setLoadingSave(false);
    }
  };

  // Función para manejar el cierre del modal de éxito
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Recargar inmuebles
    refreshData();
  };

  // Acción para eliminar un inmueble
  const handleDelete = async (inmuebleId) => {
    try {
      await inmuebleService.eliminarInmueble(inmuebleId);
      refreshData();
      message.success("Inmueble eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar el inmueble:", error);
      message.error("No se pudo eliminar el inmueble");
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  // Columnas de la tabla
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Propietario",
      dataIndex: "propietario_nombre",
      key: "propietario",
      sorter: (a, b) => {
        if (!a.propietario_nombre || !b.propietario_nombre) return 0;
        return a.propietario_nombre.localeCompare(b.propietario_nombre);
      },
      render: (text) => text || "N/A",
    },
    {
      title: "Inmueble",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => {
        if (!a.nombre || !b.nombre) return 0;
        return a.nombre.localeCompare(b.nombre);
      },
      render: (text) => text || "N/A",
    },
    {
      title: "Dirección",
      dataIndex: "direccion",
      key: "direccion",
      sorter: (a, b) => {
        if (!a.direccion || !b.direccion) return 0;
        return a.direccion.localeCompare(b.direccion);
      },
      render: (text) => text || "N/A",
    },
    {
      title: "Tipo",
      dataIndex: "tipo_inmueble",
      key: "tipo_inmueble",
      sorter: (a, b) => {
        if (!a.tipo_inmueble || !b.tipo_inmueble) return 0;
        return a.tipo_inmueble.localeCompare(b.tipo_inmueble);
      },
      render: (text) => text || "N/A",
    },
    {
      title: "Cantidad de Pisos",
      dataIndex: "cantidad_pisos",
      key: "cantidad_pisos",
      sorter: (a, b) => (a.cantidad_pisos || 0) - (b.cantidad_pisos || 0),
      render: (text) => text || "0",
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (text, record) => (
        <div className="text-center">
          {windowWidth > 768 ? (
            <>
              <BaseButton
                type="button"
                variant="primary"
                size="sm"
                className="me-2"
                icon={<EyeOutlined />}
                onClick={() => handleView(record.id)}
                title="Ver detalles"
              />
              <BaseButton
                type="button"
                variant="warning"
                size="sm"
                className="me-2"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record.id)}
                title="Editar inmueble"
              />
              {canDelete && (
                <BaseButton
                  type="button"
                  variant="danger"
                  size="sm"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                  title="Eliminar inmueble"
                />
              )}
            </>
          ) : (
            <div className="dropdown dropdown-action">
              <Link
                to="#"
                className="action-icon dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fas fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link className="dropdown-item" to="#" onClick={() => handleView(record.id)}>
                  <i className="far fa-eye me-2" />
                  Ver
                </Link>
                <Link className="dropdown-item" to="#" onClick={() => handleEdit(record.id)}>
                  <i className="far fa-edit me-2" />
                  Editar
                </Link>
                {canDelete && (
                <Link className="dropdown-item" to="#" onClick={() => handleDelete(record.id)}>
                  <i className="fa fa-trash-alt me-2" />
                  Eliminar
                </Link>
                )}
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Header />
      <Sidebar id='menu-item3' id1='menu-items3' activeClassName='inmueble-registros' />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Inmuebles </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FiChevronRight />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Lista de Inmuebles</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="doctor-table-blk">
                          <h3>Lista de Inmuebles</h3>
                          <div className="doctor-search-blk">
                            <div className="top-nav-search table-search-blk">
                              <BaseInput
                                placeholder="Buscar por nombre, dirección..."
                                value={searchText}
                                onChange={handleSearch}
                                wrapperClassName="mb-0"
                              />
                            </div>
                            <div className="add-group">
                              <BaseButton
                                type="button"
                                variant="primary"
                                className="ms-2 add-pluss"
                                title="Añadir inmueble"
                                icon={<img src={plusicon} alt="Añadir" />}
                                onClick={() => navigate('/inmueble-anadir')}
                              />
                              <BaseButton
                                type="button"
                                variant="primary"
                                className="ms-2 doctor-refresh"
                                onClick={refreshData}
                                title="Actualizar datos"
                                loading={loading}
                                icon={<FiRefreshCw />}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/*<div className="col-auto text-end float-end ms-auto download-grp">
                        <button className="btn btn-outline-primary me-2" title="Exportar a PDF">
                          <i className="fas fa-file-pdf"></i>
                        </button>
                        <button className="btn btn-outline-primary me-2" title="Exportar a Excel">
                          <i className="fas fa-file-excel"></i>
                        </button>
                      </div>*/}
                    </div>
                  </div>
                  
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}
                  
                  <div className="table-responsive doctor-list">
                    <Table
                      columns={columns}
                      dataSource={inmueblesFiltrados}
                      rowKey="id"
                      loading={loading}
                      pagination={{
                        total: inmueblesFiltrados.length,
                        showTotal: (total, range) =>
                          `Mostrando ${range[0]} a ${range[1]} de ${total} registros`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal para Ver Detalle */}
      <Modal
        title="Detalles del Inmueble"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <BaseButton
            key="cerrar"
            type="button"
            variant="secondary"
            onClick={() => setViewModalVisible(false)}
          >
            Cerrar
          </BaseButton>
        ]}
        width={700}
      >
        {inmuebleSeleccionado && (
          <div className="inmueble-details">
            <div className="row mb-3">
              <div className="col-md-6">
                <h5 className="text-primary">Información General</h5>
                <div className="detail-item">
                  <strong>Nombre:</strong> {inmuebleSeleccionado.nombre}
                </div>
                <div className="detail-item">
                  <strong>Dirección:</strong> {inmuebleSeleccionado.direccion}
                </div>
                <div className="detail-item">
                  <strong>Tipo:</strong> {inmuebleSeleccionado.tipo_inmueble}
                </div>
                <div className="detail-item">
                  <strong>Cantidad de Pisos:</strong> {inmuebleSeleccionado.cantidad_pisos}
                </div>
              </div>
              <div className="col-md-6">
                <h5 className="text-primary">Información del Propietario</h5>
                <div className="detail-item">
                  <strong>Nombre:</strong> {inmuebleSeleccionado.propietario_nombre || 'No disponible'}
                </div>
                <div className="detail-item">
                  <strong>Email:</strong> {inmuebleSeleccionado.propietario_email || 'No disponible'}
                </div>
                <div className="detail-item">
                  <strong>Teléfono:</strong> {inmuebleSeleccionado.propietario_telefono || 'No disponible'}
                </div>
              </div>
            </div>
            
            <div className="row">
              <div className="col-12">
                <h5 className="text-primary">Descripción</h5>
                <div className="card">
                  <div className="card-body">
                    <p>{inmuebleSeleccionado.descripcion || 'Sin descripción disponible'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {inmuebleSeleccionado.pisos && inmuebleSeleccionado.pisos.length > 0 && (
              <div className="row mt-3">
                <div className="col-12">
                  <h5 className="text-primary">Pisos ({inmuebleSeleccionado.pisos.length})</h5>
                  <ul className="list-group">
                    {inmuebleSeleccionado.pisos.map(piso => (
                      <li key={piso.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{piso.nombre || `Piso ${piso.numero}`}</span>
                        <span className="badge bg-primary rounded-pill">
                          {piso.espacios_count || 0} espacios
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* Modal para Editar */}
      <Modal
        title="Editar Inmueble"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <BaseButton
            key="cancelar"
            type="button"
            variant="secondary"
            onClick={() => setEditModalVisible(false)}
          >
            Cancelar
          </BaseButton>,
          <BaseButton
            key="guardar"
            type="button"
            variant="primary"
            loading={loadingSave}
            icon={<SaveOutlined />}
            onClick={handleSaveEdit}
          >
            Guardar Cambios
          </BaseButton>
        ]}
        width={700}
      >
        {inmuebleSeleccionado && (
          <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
            <div className="row">
              <div className="col-md-6">
                <BaseInput
                  label="Nombre del Inmueble"
                  name="nombre"
                  required
                  value={editFormData.nombre}
                  onChange={handleEditChange}
                />
              </div>
              <div className="col-md-6">
                <BaseSelect
                  label="Propietario"
                  name="propietario_id"
                  required
                  isSearchable
                  placeholder="Seleccione un propietario"
                  options={propietarios.map((prop) => ({
                    value: prop.id,
                    label: `${prop.nombre} ${prop.apellido}`,
                  }))}
                  value={editFormData.propietario_id}
                  onChange={handleEditChange}
                />
              </div>
              <div className="col-md-12">
                <BaseInput
                  label="Dirección"
                  name="direccion"
                  required
                  value={editFormData.direccion}
                  onChange={handleEditChange}
                />
              </div>
              <div className="col-md-6">
                <BaseSelect
                  label="Tipo de Inmueble"
                  name="tipo_inmueble"
                  native
                  required
                  placeholder="Seleccione el tipo"
                  options={TIPO_INMUEBLE_OPTIONS}
                  value={editFormData.tipo_inmueble}
                  onChange={handleEditChange}
                />
              </div>
              <div className="col-md-6">
                <BaseInput
                  label="Cantidad de Pisos"
                  name="cantidad_pisos"
                  type="number"
                  min={1}
                  required
                  value={editFormData.cantidad_pisos}
                  onChange={handleEditChange}
                />
              </div>
              <div className="col-md-12">
                <BaseTextarea
                  label="Descripción"
                  name="descripcion"
                  rows={4}
                  value={editFormData.descripcion}
                  onChange={handleEditChange}
                />
              </div>
            </div>
          </form>
        )}
      </Modal>
      
      {/* Modal de éxito */}
      <Modal
        title="¡Actualización Exitosa!"
        open={showSuccessModal}
        onCancel={handleSuccessModalClose}
        footer={[
          <BaseButton
            key="ok"
            type="button"
            variant="primary"
            onClick={handleSuccessModalClose}
          >
            Aceptar
          </BaseButton>
        ]}
        centered
      >
        <div>
          <p>El inmueble se ha actualizado correctamente con los siguientes datos:</p>
          {updatedData && (
            <ul style={{ listStyleType: 'none', padding: '10px' }}>
              <li><strong>Nombre:</strong> {updatedData.nombre}</li>
              <li><strong>Dirección:</strong> {updatedData.direccion}</li>
              <li><strong>Tipo de Inmueble:</strong> {updatedData.tipo_inmueble}</li>
              <li><strong>Cantidad de Pisos:</strong> {updatedData.cantidad_pisos}</li>
              <li><strong>Descripción:</strong> {updatedData.descripcion || 'No especificada'}</li>
            </ul>
          )}
        </div>
      </Modal>
    </>
  );
};

export default InmuebleRegistros;
